import type {
  BillableReceiptLine,
  PurchaseBillDifference,
  SavePurchaseBillInput,
} from '../api/purchaseBills';
import type { PurchaseBillDifferenceKind, PurchaseBillStatus } from '../generated/enums';
import { formatAmount } from '../format/money';
import type { StatusTone } from '../vocabulary';

/**
 * The supplier's bill screen's rules (spec §16, step 3 of
 * `docs/Purchase_Sale_Outstanding_Design.md`): the draft, what stops it being saved, the request,
 * and the differences put into words. **No tax is worked out here** — the server splits it on save
 * and the screen shows what the server says; before saving it states only the sum before tax.
 */

export interface BillLineDraft {
  /** Stable across removals. Never sent. */
  key: string;
  /** The stock billed; null for a charge such as freight. */
  goodsReceiptLineId: number | null;
  /** What the line is — the design picked, or what the charge is for. */
  description: string;
  /** Shown beside a stock line: its receipt and configuration. */
  detail: string;
  hsnCode: string;
  quantity: number;
  rate: number | null;
  taxRatePct: number | null;
}

export interface BillDraft {
  supplierVendorId: number | null;
  supplierBillNumber: string;
  /** `YYYY-MM-DD`. */
  billDate: string;
  /** The total printed on their paper, when typed. */
  paperTotal: number | null;
  notes: string;
  /** Applied to every line added after it is set; each line can still differ. */
  defaultTaxRatePct: number | null;
  lines: BillLineDraft[];
}

export const maxBillLines = 100;
export const maxBillQuantity = 1000;

let sequence = 0;

const nextKey = () => {
  sequence += 1;

  return `bill-${sequence}`;
};

export function emptyBill(today: string): BillDraft {
  return {
    supplierVendorId: null,
    supplierBillNumber: '',
    billDate: today,
    paperTotal: null,
    notes: '',
    defaultTaxRatePct: null,
    lines: [],
  };
}

/**
 * A received line onto the bill: everything not yet billed, at the order's rate where there was one,
 * else the cost typed when it came in. Adding one already on the bill does nothing.
 */
export function addReceiptLine(draft: BillDraft, line: BillableReceiptLine): BillDraft {
  if (draft.lines.some((existing) => existing.goodsReceiptLineId === line.goodsReceiptLineId)) {
    return draft;
  }

  return {
    ...draft,
    lines: [
      ...draft.lines,
      {
        key: nextKey(),
        goodsReceiptLineId: line.goodsReceiptLineId,
        description: line.itemName,
        detail: `${line.goodsReceiptNumber} · ${line.variantDisplayName}`,
        hsnCode: line.hsnCode ?? '',
        quantity: line.unbilled,
        rate: line.orderRate ?? line.receivedCost,
        taxRatePct: draft.defaultTaxRatePct,
      },
    ],
  };
}

/** A charge with no stock behind it — freight, packing. */
export function addCharge(draft: BillDraft): BillDraft {
  return {
    ...draft,
    lines: [
      ...draft.lines,
      {
        key: nextKey(),
        goodsReceiptLineId: null,
        description: 'Freight',
        detail: '',
        hsnCode: '',
        quantity: 1,
        rate: null,
        taxRatePct: draft.defaultTaxRatePct,
      },
    ],
  };
}

export function updateBillLine(
  draft: BillDraft,
  key: string,
  changes: Partial<Omit<BillLineDraft, 'key'>>,
): BillDraft {
  return {
    ...draft,
    lines: draft.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
  };
}

export function removeBillLine(draft: BillDraft, key: string): BillDraft {
  return { ...draft, lines: draft.lines.filter((line) => line.key !== key) };
}

/** The sum before tax — all the screen states before saving; GST is the server's. */
export function billSubtotal(draft: BillDraft): number {
  return draft.lines.reduce((sum, line) => sum + line.quantity * (line.rate ?? 0), 0);
}

/** Why the bill cannot be saved yet, as sentences. The server refuses each of these too. */
export function billProblems(draft: BillDraft): string[] {
  const problems: string[] = [];

  if (draft.supplierVendorId === null) {
    problems.push('Choose the supplier.');
  }

  if (draft.supplierBillNumber.trim() === '') {
    problems.push('Type the bill number printed on their paper.');
  }

  if (draft.lines.length === 0) {
    problems.push('Add what the bill is for — a delivery, or a charge.');
  } else if (draft.lines.length > maxBillLines) {
    problems.push(`A bill holds at most ${maxBillLines} lines.`);
  }

  draft.lines.forEach((line, index) => {
    const where = `Line ${index + 1}:`;

    if (line.goodsReceiptLineId === null && line.description.trim() === '') {
      problems.push(`${where} say what the charge is for.`);
    }

    if (!Number.isInteger(line.quantity) || line.quantity < 1 || line.quantity > maxBillQuantity) {
      problems.push(`${where} a quantity between 1 and ${maxBillQuantity}.`);
    }

    if (line.rate === null) {
      problems.push(`${where} type the rate.`);
    } else if (line.rate < 0) {
      problems.push(`${where} a rate cannot be negative.`);
    }

    if (line.taxRatePct === null) {
      problems.push(`${where} type the GST rate — 0 if none was charged.`);
    } else if (line.taxRatePct < 0 || line.taxRatePct > 100) {
      problems.push(`${where} a GST rate is between 0 and 100.`);
    }
  });

  return problems;
}

export function toBillRequest(draft: BillDraft): SavePurchaseBillInput {
  return {
    supplierVendorId: draft.supplierVendorId ?? 0,
    supplierBillNumber: draft.supplierBillNumber.trim(),
    billDate: draft.billDate,
    paperTotal: draft.paperTotal,
    notes: draft.notes.trim() || null,
    lines: draft.lines.map((line) => ({
      goodsReceiptLineId: line.goodsReceiptLineId,
      description: line.description.trim() || null,
      hsnCode: line.hsnCode.trim() || null,
      quantity: line.quantity,
      rate: line.rate ?? 0,
      taxRatePct: line.taxRatePct ?? 0,
    })),
  };
}

/** A difference in a sentence, naming the line it is on. */
export function differenceText(
  difference: PurchaseBillDifference,
  bill: { lines: readonly { description: string }[] },
): string {
  const line =
    difference.lineIndex === null ? undefined : bill.lines[difference.lineIndex]?.description;
  const on = line ? `${line}: ` : '';
  const rupees = (value: number | null) => (value === null ? '—' : `₹${formatAmount(value)}`);

  switch (difference.kind) {
    case 'QuantityBeyondReceived':
      return `${on}billed ${difference.actual ?? 0}, but only ${difference.expected ?? 0} received and not billed elsewhere.`;
    case 'RateDiffersFromOrder':
      return `${on}billed at ${rupees(difference.actual)}; the order agreed ${rupees(difference.expected)}.`;
    case 'TotalDiffersFromPaper':
      return `Their paper says ${rupees(difference.expected)}; the lines add up to ${rupees(difference.actual)}.`;
  }
}

export interface BillCheck {
  kind: PurchaseBillDifferenceKind;
  /** What it was checked against, said as agreeing. */
  label: string;
  ok: boolean;
  /** Each difference of this kind, in a sentence. */
  differences: string[];
}

const checkLabel: Record<PurchaseBillDifferenceKind, string> = {
  TotalDiffersFromPaper: 'Matches the total on their paper',
  RateDiffersFromOrder: 'At the rates the order agreed',
  QuantityBeyondReceived: 'No more than was received',
};

/**
 * The server's differences as the three checks a bill is held against — their paper (only when its
 * total was typed), the order's rates, what was received — each agreeing or naming what does not.
 */
export function billChecks(
  differences: readonly PurchaseBillDifference[],
  bill: { paperTotal: number | null; lines: readonly { description: string }[] },
): BillCheck[] {
  const kinds: PurchaseBillDifferenceKind[] = [
    ...(bill.paperTotal === null ? [] : (['TotalDiffersFromPaper'] as const)),
    'RateDiffersFromOrder',
    'QuantityBeyondReceived',
  ];

  return kinds.map((kind) => {
    const found = differences.filter((difference) => difference.kind === kind);

    return {
      kind,
      label: checkLabel[kind],
      ok: found.length === 0,
      differences: found.map((difference) => differenceText(difference, bill)),
    };
  });
}

export const billStatusLabel: Record<PurchaseBillStatus, string> = {
  Unpaid: 'Unpaid',
  PartPaid: 'Part paid',
  Paid: 'Paid',
  Cancelled: 'Cancelled',
};

export const billStatusTone: Record<PurchaseBillStatus, StatusTone> = {
  Unpaid: 'warning',
  PartPaid: 'brand',
  Paid: 'positive',
  Cancelled: 'neutral',
};
